import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Divider,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
  useTheme,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useMutate } from "src/hooks/useFetch.hook";
import { teamChargerLicenseUrl } from "src/react-query/endPoints";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";
import { f } from "src/utils/formatTime";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb'; // Import GB locale for DD/MM/YYYY format

const SubscriptionDetailsModal = ({
  open,
  onClose,
  subscriptionData,
  teamId,
  onUpdate,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;

  const [editableData, setEditableData] = useState({
    duration: "",
    finalExpiryDate: "", // Renamed for clarity
    contractSummary: "",
    remarks: "",
  });

  const [hasBeenSaved, setHasBeenSaved] = useState(false);
  const [isDurationManuallyChanged, setIsDurationManuallyChanged] = useState(false);

  const { mutateAsync: updateLicense, isLoading } = useMutate("team");

  /**
   * Sync data ONLY when modal opens or ID changes
   */
  useEffect(() => {
    if (!open || !subscriptionData) return;

    const mappedData = {
      duration: subscriptionData.duration || "",
      finalExpiryDate: subscriptionData.final_expiry
        ? new Date(subscriptionData.final_expiry)
        : null,
      contractSummary: subscriptionData.contractSummary || "",
      remarks: subscriptionData.remarks || "",
    };

    setEditableData(mappedData);
    setIsDurationManuallyChanged(false); // Reset manual change flag

    const isComplete =
      mappedData.duration &&
      mappedData.finalExpiryDate &&
      mappedData.contractSummary.trim() &&
      mappedData.remarks.trim();

    setHasBeenSaved(!!isComplete);
  }, [open, subscriptionData?.licensePk]);

  const allFieldsComplete = useMemo(() => {
    return (
      editableData.duration &&
      editableData.finalExpiryDate &&
      editableData.contractSummary.trim() &&
      editableData.remarks.trim()
    );
  }, [editableData]);

  const isReadOnly = hasBeenSaved && allFieldsComplete;

  const handleInputChange = (field, value) => {
    setEditableData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Track if duration is manually changed by user
    if (field === "duration") {
      setIsDurationManuallyChanged(true);
    }
  };

  const handleSave = async () => {
    if (!subscriptionData?.id || !teamId) {
      dispatch(
        setAlert({
          open: true,
          message: "Invalid subscription data",
          severity: enums.SEVERITY.ERROR,
        }),
      );
      return;
    }

    // Calculate expiry date based on duration from charger creation date (matching display logic)
    const calculateExpiry = (duration) => {
      if (!subscriptionData.chargerCreationDate) return null;
      
      const baseDate = new Date(subscriptionData.chargerCreationDate);
      let expiryDate = new Date(baseDate);

      if (duration === "M") {
        // Add 1 month from charger creation date
        const currentMonth = baseDate.getMonth();
        const currentYear = baseDate.getFullYear();
        
        if (currentMonth === 11) { // December, roll over to next year
          expiryDate.setFullYear(currentYear + 1);
          expiryDate.setMonth(0); // January
        } else {
          expiryDate.setMonth(currentMonth + 1);
        }
      } else if (duration === "Y") {
        // Add 1 year from charger creation date
        expiryDate.setFullYear(baseDate.getFullYear() + 1);
      } else {
        expiryDate = baseDate;
      }

      return expiryDate.toISOString().split("T")[0]; 
    };

    try {
      if (editableData.finalExpiryDate && !editableData.duration) {
        dispatch(
          setAlert({
            open: true,
            message: "Duration is required when setting Final Expiry Date.",
            severity: enums.SEVERITY.ERROR,
          }),
        );
        return;
      }

      const calculatedExpiry = editableData.duration ? calculateExpiry(editableData.duration) : null;
      
      if (calculatedExpiry && editableData.finalExpiryDate && editableData.finalExpiryDate <= calculatedExpiry) {
        dispatch(
          setAlert({
            open: true,
            message: "Final Expiry Date must be greater than the calculated expiry date.",
            severity: enums.SEVERITY.ERROR,
          }),
        );
        return;
      }

      const requestBody = {
        license_pk: Number(subscriptionData.licensePk),
        duration: editableData.duration || null,
        summary: editableData.contractSummary,
        remarks: editableData.remarks,
        final_expiry: editableData.finalExpiryDate || null,
      };

      if (calculatedExpiry) {
        requestBody.expiry = calculatedExpiry;
      }

      await updateLicense({
        url: teamChargerLicenseUrl(teamId),
        method: "PUT",
        body: requestBody,
      });

      setHasBeenSaved(true);

      // Close modal after successful save and trigger parent refetch
      onClose();
      onUpdate?.();
    } catch (err) {
      console.error(err);
    }
  };

  if (!subscriptionData) return null;

  const getCalculatedExpiryDisplay = () => {
    if (!editableData.duration || !subscriptionData.chargerCreationDate) return "-";
    
    // Use charger creation date as base, not today's date
    const baseDate = new Date(subscriptionData.chargerCreationDate);
    let expiryDate = new Date(baseDate);
    
    if (editableData.duration === "M") {
      // Add 1 month from charger creation date
      const currentMonth = baseDate.getMonth();
      const currentYear = baseDate.getFullYear();
      
      if (currentMonth === 11) { // December, roll over to next year
        expiryDate.setFullYear(currentYear + 1);
        expiryDate.setMonth(0); // January
      } else {
        expiryDate.setMonth(currentMonth + 1);
      }
    } else if (editableData.duration === "Y") {
      // Add 1 year from charger creation date
      expiryDate.setFullYear(baseDate.getFullYear() + 1);
    } else {
      return "-";
    }
    
    return f(expiryDate, "dd/MM/yyyy");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">
          Subscription Details – {subscriptionData?.licenseId || ""}
        </Typography>
        <IconButton onClick={onClose}>
          <GridCloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" color="primary">
              License Overview
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">Team Name</Typography>
            <Typography>{subscriptionData.teamName || "-"}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">Charger ID</Typography>
            <Typography>{subscriptionData.chargerId || "-"}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">Charger Created</Typography>
            <Typography>
              {f(subscriptionData.chargerCreationDate, "dd/MM/yyyy")}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">Expiry Date</Typography>
            <Typography>
              {(editableData.duration && isDurationManuallyChanged)
                ? getCalculatedExpiryDisplay() // Show calculated expiry when user manually changes duration
                : subscriptionData.chargerSubscriptionExpiryDate 
                ? f(subscriptionData.chargerSubscriptionExpiryDate, "dd/MM/yyyy") // Show original expiry date
                : "-" // Show dash if no data
              }
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" color="primary">
              Subscription Settings
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Duration</InputLabel>
              <Select
                value={editableData.duration}
                label="Duration"
                disabled={isReadOnly}
                onChange={(e) => handleInputChange("duration", e.target.value)}
              >
                <MenuItem value="M">Monthly</MenuItem>
                <MenuItem value="Y">Yearly</MenuItem>
                <MenuItem value="O">One Time</MenuItem>
                <MenuItem value="T" disabled>Trial</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                fullWidth
                size="small"
                label="Final Expiry Date"
                disabled={isReadOnly}
                value={editableData.finalExpiryDate ? dayjs(editableData.finalExpiryDate) : null}
                onChange={(newValue) => {
                  const selectedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                  handleInputChange("finalExpiryDate", selectedDate);
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Contract Summary
            </Typography>
            <TextareaAutosize
              minRows={4}
              style={{
                width: "100%",
                borderRadius: 8,
                padding: 12,
                fontFamily: "inherit",
                fontSize: 14,
                borderColor: theme.palette.divider,
                backgroundColor: isReadOnly ? theme.palette.action.disabledBackground : theme.palette.background.paper,
                color: theme.palette.text.primary,
                resize: "vertical",
              }}
              disabled={isReadOnly}
              value={editableData.contractSummary}
              onChange={(e) =>
                handleInputChange("contractSummary", e.target.value)
              }
              placeholder="Enter contract summary"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Remarks
            </Typography>
            <TextareaAutosize
              minRows={4}
              style={{
                width: "100%",
                borderRadius: 8,
                padding: 12,
                fontFamily: "inherit",
                fontSize: 14,
                borderColor: theme.palette.divider,
                backgroundColor: isReadOnly ? theme.palette.action.disabledBackground : theme.palette.background.paper,
                color: theme.palette.text.primary,
                resize: "vertical",
              }}
              disabled={isReadOnly}
              value={editableData.remarks}
              onChange={(e) => handleInputChange("remarks", e.target.value)}
              placeholder="Enter remarks"
            />
          </Grid>

          {isReadOnly && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                <Typography variant="body2">
                  ℹ️ Subscription details saved. Fields are read-only.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {!isReadOnly && (
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleSave}
          >
            Save Changes
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDetailsModal;
